import { isProductionEnvironment } from "@/helper/app/browser-helper"
import {
  getAppInsightsClient,
  isAppInsightsConsoleLoggingEnabled
} from "@/services/application-insights-service"

const MIN_BLOCK_MS = 100
const DEFAULT_FLUSH_MINUTES = 1

interface UiBlockSample {
  durationMs: number
  route: string
}

interface UiBlockTrackingOptions {
  getTenant: () => string
  getRoute: () => string
  flushIntervalMinutes?: number
}

let pendingBlocks: UiBlockSample[] = []
let flushTimer: ReturnType<typeof setInterval> | undefined
let observer: PerformanceObserver | undefined
let started = false

function flushIntervalMs(options: UiBlockTrackingOptions): number {
  const fromEnv = Number(import.meta.env.VITE_APPINSIGHTS_UI_BLOCK_FLUSH_MINUTES)
  if (Number.isFinite(fromEnv) && fromEnv > 0) {
    return fromEnv * 60 * 1000
  }

  return (options.flushIntervalMinutes ?? DEFAULT_FLUSH_MINUTES) * 60 * 1000
}

function percentile(values: number[], pct: number): number | null {
  if (values.length === 0) return null
  const sorted = [...values].sort((a, b) => a - b)
  const index = Math.ceil((pct / 100) * sorted.length) - 1
  return sorted[Math.max(0, index)]
}

function normalizeRoute(route: string): string {
  const trimmed = route.trim()
  return trimmed || "unknown"
}

function normalizeTenant(tenant: string): string {
  const trimmed = tenant.trim()
  return trimmed || "unknown"
}

export function flushUiBlockSummary(getTenant: () => string) {
  if (pendingBlocks.length === 0) {
    return
  }

  const tenant = normalizeTenant(getTenant())
  const byRoute = new Map<string, number[]>()

  for (const block of pendingBlocks) {
    const route = normalizeRoute(block.route)
    const durations = byRoute.get(route) ?? []
    durations.push(block.durationMs)
    byRoute.set(route, durations)
  }

  pendingBlocks = []

  for (const [route, durations] of byRoute) {
    const totalBlockedMs = durations.reduce((sum, value) => sum + value, 0)
    const maxBlockedMs = Math.max(...durations)
    const p95BlockedMs = percentile(durations, 95) ?? maxBlockedMs

    if (isAppInsightsConsoleLoggingEnabled()) {
      console.info("[app-insights][local] would send UI blocks", {
        tenant,
        route,
        blockCount: durations.length,
        totalBlockedMs: Math.round(totalBlockedMs),
        maxBlockedMs: Math.round(maxBlockedMs),
        p95BlockedMs: Math.round(p95BlockedMs),
        durations
      })
      continue
    }

    const appInsights = getAppInsightsClient()
    if (!appInsights) {
      return
    }

    appInsights.trackEvent(
      {
        name: "ui_block_summary",
        measurements: {
          blockCount: durations.length,
          totalBlockedMs: Math.round(totalBlockedMs),
          maxBlockedMs: Math.round(maxBlockedMs),
          p95BlockedMs: Math.round(p95BlockedMs)
        }
      },
      { tenant, route }
    )

    for (const durationMs of durations) {
      appInsights.trackEvent(
        {
          name: "ui_block",
          measurements: { durationMs }
        },
        { tenant, route }
      )
    }
  }
}

function recordLongTask(durationMs: number, getRoute: () => string) {
  if (durationMs < MIN_BLOCK_MS) {
    return
  }

  pendingBlocks.push({
    durationMs: Math.round(durationMs),
    route: getRoute()
  })
}

function stopUiBlockTracking() {
  if (flushTimer !== undefined) {
    clearInterval(flushTimer)
    flushTimer = undefined
  }

  observer?.disconnect()
  observer = undefined
  started = false
}

export function startUiBlockTracking(options: UiBlockTrackingOptions) {
  const consoleLoggingEnabled = isAppInsightsConsoleLoggingEnabled()

  if (!isProductionEnvironment() && !consoleLoggingEnabled) {
    return
  }

  if (started || typeof PerformanceObserver === "undefined") {
    return
  }

  if (!consoleLoggingEnabled && !getAppInsightsClient()) {
    return
  }

  started = true

  try {
    observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        recordLongTask(entry.duration, options.getRoute)
      }
    })
    observer.observe({ type: "longtask", buffered: true })
  } catch {
    started = false
    return
  }

  const flush = () => flushUiBlockSummary(options.getTenant)

  flushTimer = setInterval(flush, flushIntervalMs(options))

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flush()
    }
  })

  window.addEventListener("pagehide", flush)
  window.addEventListener("beforeunload", flush)
}

export function stopUiBlockTrackingForTests() {
  stopUiBlockTracking()
  pendingBlocks = []
}
